import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { siteConfig } from '@/mocks/data';
import { useState } from 'react';

export default function AboutPage() {
  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `关于我们 - ${siteConfig.name}`,
          description: siteConfig.brand.intro,
          url: `${siteConfig.url}/about`,
          inLanguage: 'zh-CN',
        }}
      />
      <SEOHead
        title={`关于我们 - ${siteConfig.name}`}
        description={siteConfig.brand.intro}
        keywords={`${siteConfig.name},关于我们,成人资源导航,安全导航平台`}
        canonicalUrl={`${siteConfig.url}/about`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <div className="px-4 md:px-6 pb-6">
                <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                  关于我们
                </h1>
              </div>
            </div>
          </div>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">
                  品牌介绍
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {siteConfig.brand.intro}
                </p>
                <div className="space-y-3 mt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                      <i className="ri-flag-line text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">我们的使命</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">{siteConfig.brand.mission}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                      <i className="ri-eye-line text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">我们的愿景</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">{siteConfig.brand.vision}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20clean%20office%20workspace%20with%20warm%20coral%20and%20cream%20color%20scheme%2C%20professional%20team%20atmosphere%2C%20minimalist%20design%2C%20soft%20natural%20lighting%2C%20plants%20and%20modern%20furniture%2C%20abstract%20geometric%20wall%20art&width=700&height=500&seq=about-team-image&orientation=landscape"
                  alt="安心导航团队"
                  title="安心导航 - 专业团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-10">
                核心优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {siteConfig.brand.values.map((v) => (
                  <div
                    key={v.title}
                    className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70"
                  >
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 font-heading mb-2">
                      {v.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                      {v.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-10">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 max-w-5xl mx-auto">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-600">{siteConfig.brand.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">联系电话</h4>
                    <p className="text-sm text-foreground-600">{siteConfig.brand.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">办公地址</h4>
                    <p className="text-sm text-foreground-600">{siteConfig.brand.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                    <p className="text-sm text-foreground-600">{siteConfig.brand.contact.wechat}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70">
                <h3 className="text-base font-semibold text-foreground-900 font-heading mb-4">
                  给我们留言
                </h3>
                <AboutContactForm />
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}

function AboutContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('submitting');
    setFormError('');

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          urlEncoded.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9q7mecnlsngrm4llsug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
      <div className="anti-spam-field">
        <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
      </div>
      <div>
        <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1">
          姓名
        </label>
        <input
          id="about-name"
          name="name"
          type="text"
          required
          disabled={formStatus === 'submitting'}
          className="w-full px-3 py-2 text-sm bg-background-50 border border-background-300/60 rounded-md text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1">
          邮箱
        </label>
        <input
          id="about-email"
          name="email"
          type="email"
          required
          disabled={formStatus === 'submitting'}
          className="w-full px-3 py-2 text-sm bg-background-50 border border-background-300/60 rounded-md text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
          placeholder="请输入您的邮箱"
        />
      </div>
      <div>
        <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1">
          留言内容
        </label>
        <textarea
          id="about-message"
          name="message"
          required
          rows={4}
          maxLength={500}
          disabled={formStatus === 'submitting'}
          className="w-full px-3 py-2 text-sm bg-background-50 border border-background-300/60 rounded-md text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
          placeholder="请输入您的留言内容（最多500字）"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
      </div>

      {formStatus === 'success' && (
        <div className="flex items-center gap-2 p-3 bg-accent-50 text-accent-700 rounded-md text-sm">
          <i className="ri-checkbox-circle-line"></i>
          感谢您的留言！我们会尽快通过邮件回复您。
        </div>
      )}
      {formStatus === 'error' && formError && (
        <div className="flex items-center gap-2 p-3 bg-primary-50 text-primary-700 rounded-md text-sm">
          <i className="ri-error-warning-line"></i>
          {formError}
        </div>
      )}

      <button
        type="submit"
        disabled={formStatus === 'submitting'}
        className="w-full px-4 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {formStatus === 'submitting' ? '发送中...' : '发送留言'}
      </button>
    </form>
  );
}